import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { getCompanion } from '@/lib/companion/registry';
import { normalizeManifest } from '@/lib/companion/manifest-loader';
import { measureSpriteBounds, type SpriteBounds } from '@/lib/companion/measure-sprite-bounds';
import styles from './companion-thumbnail.module.css';

const DEFAULT_SIZE = 72;

export interface CompanionThumbnailProps {
  companionId: string;
  size?: number;
}

/** Scales the frame so the sprite's opaque bounds (not the frame box) fill
 *  the tile, centered. Every companion then reads at the same visual size
 *  regardless of how much transparent padding its atlas carries. */
function destinationRect(frameW: number, frameH: number, bounds: SpriteBounds, tileSize: number) {
  const scale = Math.min(tileSize / bounds.width, tileSize / bounds.height);
  const dw = frameW * scale;
  const dh = frameH * scale;
  const dx = tileSize / 2 - (bounds.x + bounds.width / 2) * scale;
  const dy = tileSize / 2 - (bounds.y + bounds.height / 2) * scale;
  return { dx, dy, dw, dh };
}

/** A still portrait: frame 0 of the idle row, drawn once. Animating every
 *  companion in the gallery would decode N multi-megabyte sheets and run N
 *  loops for a settings pane. */
export function CompanionThumbnail({ companionId, size = DEFAULT_SIZE }: CompanionThumbnailProps) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);

  const entry = getCompanion(companionId);

  const frame = useMemo(() => {
    if (!entry) return null;
    return {
      width: entry.raw.frameWidth ?? 192,
      height: entry.raw.frameHeight ?? 208,
    };
  }, [entry]);

  useEffect(() => {
    if (!entry || !frame || !canvas) return;

    let cancelled = false;
    const img = new Image();

    const draw = () => {
      if (cancelled) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const manifest = normalizeManifest(entry.raw, {
        spritesheet: entry.spritesheet,
        atlasWidth: img.naturalWidth,
        atlasHeight: img.naturalHeight,
        fallbackId: entry.id,
      });
      const idle = manifest.animations.idle ?? { row: 0, frames: 1 };
      const sy = idle.row * frame.height;

      const bounds = measureSpriteBounds(img, 0, sy, frame.width, frame.height)
        ?? { x: 0, y: 0, width: frame.width, height: frame.height };
      const { dx, dy, dw, dh } = destinationRect(frame.width, frame.height, bounds, size);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, sy, frame.width, frame.height, dx, dy, dw, dh);
    };

    img.addEventListener('load', draw);
    img.src = entry.spritesheet;
    if (img.complete && img.naturalWidth > 0) draw();

    return () => { cancelled = true; img.removeEventListener('load', draw); };
  }, [entry, frame, canvas, size]);

  if (!entry || !frame) return null;

  return (
    <canvas
      ref={setCanvas}
      className={styles.canvas}
      width={size}
      height={size}
      aria-label={entry.raw.displayName ?? entry.id}
      style={{
        '--thumb-w': `${size}px`,
        '--thumb-h': `${size}px`,
      } as React.CSSProperties}
    />
  );
}
